// agoraLocalPublishing.js
'use strict';

import { player as playerDebug } from '../resource/debug.js';
import {
  getAgoraInstance,
  loadAgoraScript,
  AGORA_CLIENT_ROLE,
  AGORA_AUDIENCE_LATENCY_LEVEL,
  AGORA_CLIENT_CONNECTION_STATE,
} from '../resource/agora.js';
import {
  ensureLocalTracks as ensureAgoraLocalTracks,
  ensureLocalTrack as ensureAgoraLocalTrack,
  releaseLocalTracks,
} from '../resource/agoraLocalTracks.js';

const log = playerDebug.extend('log:agoraLocalPublishing');
const errorLog = playerDebug.extend('error:agoraLocalPublishing');

// Viewer camera publish state. Device tracks live in agoraLocalTracks.js.
const LOCAL_PUBLISH_STATE = {
  IDLE: 'idle',
  STARTING: 'starting',
  PUBLISHING: 'publishing',
};

// Identifies the failed publish step for tracking.
const LOCAL_PUBLISH_STEP = {
  WAIT_CHANNEL_READY: 'waitChannelReady',
  GET_CLIENT: 'getClient',
  SET_CLIENT_ROLE: 'setClientRole',
  PUBLISH: 'publish',
};
// Opaque per-channel publish entry, compared with === only: an in-flight
// publish holds the entry it started with, and deleting the channel's entry
// (stop/teardown) cancels every step that re-checks it afterwards.
const localPublishEntries = new Map();
const getOrCreateLocalPublishEntry = channelId => {
  let entry = localPublishEntries.get(channelId);
  if (!entry) {
    entry = {};
    localPublishEntries.set(channelId, entry);
  }
  return entry;
};
const invalidateLocalPublish = channelId =>
  localPublishEntries.delete(channelId);

/**
 * Release device tracks and cancel any in-flight publish for a channel.
 * Device ownership is independent of an Agora instance/client, so this is
 * safe to call even when the channel failed before a client existed.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {undefined} No return value.
 * @example
 * releaseLocalPublishing({ channelId: 'session-id' });
 */
const releaseLocalPublishing = ({ channelId } = {}) => {
  releaseLocalTracks({ channelId });
  invalidateLocalPublish(channelId);
};

const WAIT_FOR_CHANNEL_READY_TIMEOUT_MS = 10000;
const WAIT_FOR_CHANNEL_READY_INTERVAL_MS = 1000;

// The shared client may still be reconnecting after a token refresh. A
// missing instance also means "not yet", not "never": re-entering a room
// mid-PERFORMING can make the viewer camera ready before the player has
// even acquired the instance, so keep polling instead of failing fast
// (a teardown mid-wait is stopped by isCancelled, not by this check).
const waitForChannelReady = async ({ channelId, isCancelled } = {}) => {
  const deadline = Date.now() + WAIT_FOR_CHANNEL_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (isCancelled?.()) return false;
    if (
      getAgoraInstance({ channelId })?.client?.connectionState ===
      AGORA_CLIENT_CONNECTION_STATE.CONNECTED
    ) {
      return true;
    }
    await new Promise(resolve =>
      setTimeout(resolve, WAIT_FOR_CHANNEL_READY_INTERVAL_MS)
    );
  }
  return false;
};

const createLocalPublishError = ({ message, step }) => {
  const error = new Error(message);
  error.viewerCameraStep = step;
  return error;
};

const resetLocalPublishState = agoraInstance => {
  agoraInstance.localPublishState = LOCAL_PUBLISH_STATE.IDLE;
  agoraInstance.localPublishedTracks = null;
  agoraInstance.localPublishPromise = null;
};

const resetToAudienceRole = async agoraInstance => {
  const { channelId, client } = agoraInstance;
  if (!client) return;
  const setAudienceRole = () =>
    client.setClientRole(AGORA_CLIENT_ROLE.AUDIENCE, {
      level: AGORA_AUDIENCE_LATENCY_LEVEL,
    });
  try {
    await setAudienceRole();
  } catch (error) {
    errorLog('resetToAudienceRole: failed, retrying', { channelId, error });
    try {
      await setAudienceRole();
    } catch (retryError) {
      // The role may stay HOST even though the publish state is reset to
      // IDLE; surface it loudly instead of failing silently.
      errorLog('resetToAudienceRole: retry failed, role may stay host', {
        channelId,
        error: retryError,
      });
    }
  }
};

// A cancelled attempt must not downgrade a newer publish.
const resetToAudienceRoleIfIdle = agoraInstance => {
  if (agoraInstance.localPublishState !== LOCAL_PUBLISH_STATE.IDLE) return;
  return resetToAudienceRole(agoraInstance);
};

const unpublishTracks = async ({ client, tracks, channelId }) => {
  if (!tracks.length) return;
  try {
    await client.unpublish(tracks);
  } catch (error) {
    errorLog('unpublishTracks: failed', { channelId, error });
  }
};

/**
 * Load Agora SDK and get or reuse local device tracks without publishing.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {Promise<{tracks: {audioTrack: object|null, videoTrack: object|null}, failures: Array<{trackType: string, error: *}>, entry: object}|null>} Capture result.
 * @example
 * const { tracks, failures } = await ensureLocalTracks({
 *   channelId: 'session-id',
 * });
 */
const ensureLocalTracks = async ({ channelId } = {}) => {
  await loadAgoraScript();
  return ensureAgoraLocalTracks({ channelId });
};

/**
 * Load Agora SDK and get or create one local device track.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @param {'audio'|'video'} params.trackType - Track type to acquire.
 * @returns {Promise<object|null>} Local track when available.
 * @example
 * const track = await ensureLocalTrack({
 *   channelId: 'session-id',
 *   trackType: 'video',
 * });
 */
const ensureLocalTrack = async ({ channelId, trackType } = {}) => {
  await loadAgoraScript();
  return ensureAgoraLocalTrack({ channelId, trackType });
};

// Agora rejects publishing a disabled track, and a track the user turned off
// must stay private; treat it as absent until setEnabled(true) republishes it.
const getPublishableLocalTrack = track =>
  track && track.enabled !== false ? track : null;

const getPublishableLocalTracks = ({ audioTrack, videoTrack } = {}) =>
  [audioTrack, videoTrack].filter(Boolean);

const getUnpublishedLocalTracks = ({ localTracks, publishedTracks } = {}) =>
  Object.entries(localTracks ?? {})
    .filter(
      ([trackKey, track]) => track && publishedTracks?.[trackKey] !== track
    )
    .map(([, track]) => track);

const publishNewLocalTracks = async ({
  agoraInstance,
  channelId,
  localTracks,
  publishEntry,
} = {}) => {
  const isCancelled = () => localPublishEntries.get(channelId) !== publishEntry;
  while (agoraInstance.localPublishPromise) {
    const inFlightPublishPromise = agoraInstance.localPublishPromise;
    try {
      await inFlightPublishPromise;
    } catch (error) {
      if (isCancelled()) return null;
      throw error;
    }
    if (isCancelled()) return null;
  }
  if (
    isCancelled() ||
    agoraInstance.localPublishState !== LOCAL_PUBLISH_STATE.PUBLISHING
  ) {
    return null;
  }

  const unpublishedTracks = getUnpublishedLocalTracks({
    localTracks,
    publishedTracks: agoraInstance.localPublishedTracks,
  });
  if (!unpublishedTracks.length) return agoraInstance.localPublishedTracks;

  const { client } = agoraInstance;
  const publishNewTracksPromise = (async () => {
    try {
      await client.publish(unpublishedTracks);
      if (isCancelled()) {
        await unpublishTracks({
          client,
          tracks: unpublishedTracks,
          channelId,
        });
        return null;
      }
      // A null track key must not erase a previously published track.
      agoraInstance.localPublishedTracks = {
        ...agoraInstance.localPublishedTracks,
        ...Object.fromEntries(
          Object.entries(localTracks ?? {}).filter(([, track]) => track)
        ),
      };
      log('publishLocalTracks: added tracks', {
        channelId,
        count: unpublishedTracks.length,
      });
      return agoraInstance.localPublishedTracks;
    } catch (error) {
      if (error && typeof error === 'object') {
        error.viewerCameraStep = LOCAL_PUBLISH_STEP.PUBLISH;
      }
      errorLog('publishLocalTracks: add tracks failed', { channelId, error });
      throw error;
    }
  })();
  agoraInstance.localPublishPromise = publishNewTracksPromise;

  try {
    return await publishNewTracksPromise;
  } finally {
    if (agoraInstance.localPublishPromise === publishNewTracksPromise) {
      agoraInstance.localPublishPromise = null;
    }
  }
};

/**
 * Publish prepared viewer tracks. Returns null when unavailable or cancelled.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @param {{audioTrack: object|null, videoTrack: object|null}} params.localTracks - Available tracks to publish.
 * @returns {Promise<{audioTrack: object|null, videoTrack: object|null}|null>} Published tracks when successful.
 * @example
 * const publishedTracks = await publishLocalTracks({ channelId, localTracks });
 */
const publishLocalTracks = async ({ channelId, localTracks } = {}) => {
  // The registry object is mutable; keep this request independent from tracks
  // added while an earlier publish is still pending.
  const requestedLocalTracks = {
    audioTrack: getPublishableLocalTrack(localTracks?.audioTrack),
    videoTrack: getPublishableLocalTrack(localTracks?.videoTrack),
  };
  const tracks = getPublishableLocalTracks(requestedLocalTracks);
  if (!tracks.length) {
    errorLog('publishLocalTracks: no publishable local tracks', { channelId });
    return null;
  }
  // Capture before the first await so stop can cancel every later step.
  const publishEntry = getOrCreateLocalPublishEntry(channelId);
  const isCancelled = () => localPublishEntries.get(channelId) !== publishEntry;

  const isChannelReady = await waitForChannelReady({ channelId, isCancelled });
  if (!isChannelReady) {
    if (isCancelled()) return null;
    errorLog('publishLocalTracks: channel not ready (timeout)', {
      channelId,
    });
    // Drop the publish entry created above so a failed attempt does not linger.
    invalidateLocalPublish(channelId);
    throw createLocalPublishError({
      message: 'Agora channel not ready',
      step: LOCAL_PUBLISH_STEP.WAIT_CHANNEL_READY,
    });
  }
  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance?.client) {
    if (isCancelled()) return null;
    errorLog('publishLocalTracks: agora client not found', { channelId });
    invalidateLocalPublish(channelId);
    throw createLocalPublishError({
      message: 'Agora client not found',
      step: LOCAL_PUBLISH_STEP.GET_CLIENT,
    });
  }
  if (isCancelled()) return null;
  // A stop or an earlier publish may still be in flight; wait for it to settle
  // so the state below is the final one, not a transient snapshot.
  while (agoraInstance.localPublishPromise) {
    await agoraInstance.localPublishPromise.catch(() => {});
    if (isCancelled()) return null;
  }
  if (agoraInstance.localPublishState !== LOCAL_PUBLISH_STATE.IDLE) {
    return publishNewLocalTracks({
      agoraInstance,
      channelId,
      localTracks: requestedLocalTracks,
      publishEntry,
    });
  }
  agoraInstance.localPublishState = LOCAL_PUBLISH_STATE.STARTING;

  const { client } = agoraInstance;
  const initialPublishPromise = (async () => {
    let publishStep = LOCAL_PUBLISH_STEP.SET_CLIENT_ROLE;
    try {
      // Audience clients must become hosts before publishing.
      await client.setClientRole(AGORA_CLIENT_ROLE.HOST);
      if (isCancelled()) {
        await resetToAudienceRoleIfIdle(agoraInstance);
        return null;
      }

      publishStep = LOCAL_PUBLISH_STEP.PUBLISH;
      await client.publish(tracks);
      if (isCancelled()) {
        await unpublishTracks({
          client,
          tracks,
          channelId,
        });
        await resetToAudienceRoleIfIdle(agoraInstance);
        return null;
      }
      agoraInstance.localPublishState = LOCAL_PUBLISH_STATE.PUBLISHING;
      agoraInstance.localPublishedTracks = { ...requestedLocalTracks };
      agoraInstance.localPublishedAt = Date.now();
      log('publishLocalTracks: published', { channelId });
      return agoraInstance.localPublishedTracks;
    } catch (error) {
      if (error && typeof error === 'object') {
        error.viewerCameraStep = publishStep;
      }
      errorLog('publishLocalTracks: failed', {
        channelId,
        step: publishStep,
        error,
      });
      if (isCancelled()) {
        await resetToAudienceRoleIfIdle(agoraInstance);
      } else {
        await unpublishLocalTracks({ channelId });
      }
      throw error;
    }
  })();
  agoraInstance.localPublishPromise = initialPublishPromise;

  try {
    return await initialPublishPromise;
  } finally {
    if (agoraInstance.localPublishPromise === initialPublishPromise) {
      agoraInstance.localPublishPromise = null;
    }
  }
};

/**
 * Stop publishing viewer tracks and restore the audience role.
 * @param {object} params
 * @param {string} params.channelId - Agora channel id.
 * @returns {Promise<{publishedMs: number|undefined}>} Published duration when tracks were published.
 * @example
 * const { publishedMs } = await unpublishLocalTracks({ channelId: 'session-id' });
 */
const unpublishLocalTracks = async ({ channelId } = {}) => {
  // Also cancels a publish that is still waiting for the channel.
  invalidateLocalPublish(channelId);

  const agoraInstance = getAgoraInstance({ channelId });
  if (!agoraInstance) return { publishedMs: undefined };

  const publishedMs = agoraInstance.localPublishedAt
    ? Date.now() - agoraInstance.localPublishedAt
    : undefined;
  agoraInstance.localPublishedAt = null;

  if (!agoraInstance.client) {
    resetLocalPublishState(agoraInstance);
    return { publishedMs };
  }

  const { client, localPublishedTracks } = agoraInstance;
  const tracksToUnpublish = [
    localPublishedTracks?.audioTrack,
    localPublishedTracks?.videoTrack,
  ].filter(Boolean);

  const unpublishPromise = (async () => {
    if (agoraInstance.localPublishState === LOCAL_PUBLISH_STATE.PUBLISHING) {
      await unpublishTracks({ client, tracks: tracksToUnpublish, channelId });
    }
    if (agoraInstance.localPublishState !== LOCAL_PUBLISH_STATE.IDLE) {
      await resetToAudienceRole(agoraInstance);
    }
    resetLocalPublishState(agoraInstance);
  })();
  // Publish requests arriving mid-stop wait on this before reading the state,
  // so they cannot mistake the stale PUBLISHING state for an active publish.
  agoraInstance.localPublishPromise = unpublishPromise;
  try {
    await unpublishPromise;
  } finally {
    if (agoraInstance.localPublishPromise === unpublishPromise) {
      agoraInstance.localPublishPromise = null;
    }
  }
  log('unpublishLocalTracks: done', { channelId });
  return { publishedMs };
};

// Re-check publish state after async action work.
const getIsLocalPublishing = ({ channelId } = {}) =>
  getAgoraInstance({ channelId })?.localPublishState ===
  LOCAL_PUBLISH_STATE.PUBLISHING;

export {
  LOCAL_PUBLISH_STATE,
  LOCAL_PUBLISH_STEP,
  releaseLocalPublishing,
  ensureLocalTracks,
  ensureLocalTrack,
  publishLocalTracks,
  unpublishLocalTracks,
  getIsLocalPublishing,
};
